/**
 * Agency Portal v2 §4.7 — WCAG contrast helper for the accent-colour
 * picker. The accent renders on CTA buttons; the button text colour is
 * chosen automatically between ink and white, picking whichever hits
 * ≥ 4.5:1 against the accent. If neither reaches 3:1, the editor shows
 * a low-contrast warning (save is NOT blocked — the agency may have a
 * stronger reason to use their colour than our contrast heuristic).
 *
 * Pure functions; no DOM, no theme tokens. Unit-tested.
 */

/** Parse a `#rrggbb` string into [r, g, b] in 0..1 space. Returns null
 *  for anything that isn't exactly a 6-digit hex. */
export function parseHex6(input: string): readonly [number, number, number] | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(trimmed)) return null;
  const r = parseInt(trimmed.slice(1, 3), 16) / 255;
  const g = parseInt(trimmed.slice(3, 5), 16) / 255;
  const b = parseInt(trimmed.slice(5, 7), 16) / 255;
  return [r, g, b];
}

/** sRGB → linear transform (WCAG 2.1 §1.4.3). */
function srgbToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Relative luminance of an sRGB colour (0..1). */
export function relativeLuminance(rgb: readonly [number, number, number]): number {
  const [r, g, b] = rgb;
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
}

/** Contrast ratio between two sRGB colours (1..21). */
export function contrastRatio(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick a readable text colour (ink / dark background in dark mode is
 * light; the Sill palette uses `#1a1410` for ink and `#ffffff` for
 * white) for a given accent background. Returns the one with the
 * higher contrast ratio. If neither hits WCAG AA (4.5:1), the caller
 * can decide whether to warn — see `wcagAssessAgainstTheme`.
 */
export const INK: readonly [number, number, number] = [26 / 255, 20 / 255, 16 / 255];
export const WHITE: readonly [number, number, number] = [1, 1, 1];

export function pickTextOnAccent(accentHex: string): 'ink' | 'white' {
  const rgb = parseHex6(accentHex);
  if (rgb === null) return 'ink';
  const vsInk = contrastRatio(rgb, INK);
  const vsWhite = contrastRatio(rgb, WHITE);
  return vsInk >= vsWhite ? 'ink' : 'white';
}

export interface ContrastAssessment {
  readonly against_ink: number;
  readonly against_white: number;
  readonly best: number;
  readonly text_colour: 'ink' | 'white';
  /** `pass_aa`: at least one background pair hits 4.5:1 (WCAG AA for
   *  normal text — buttons use normal text). `low`: neither reaches
   *  3:1 (WCAG AA large / UI minimum). `warn`: between 3:1 and 4.5:1. */
  readonly tier: 'pass_aa' | 'warn' | 'low';
}

/**
 * Assess the accent against both backgrounds (so a dark-mode preview
 * is covered too), returning the best contrast + the chosen text
 * colour + a tier label the editor renders as a warning or clear.
 */
export function wcagAssessAgainstTheme(accentHex: string): ContrastAssessment | null {
  const rgb = parseHex6(accentHex);
  if (rgb === null) return null;
  const against_ink = contrastRatio(rgb, INK);
  const against_white = contrastRatio(rgb, WHITE);
  const text_colour: 'ink' | 'white' = against_ink >= against_white ? 'ink' : 'white';
  const best = Math.max(against_ink, against_white);
  const tier: ContrastAssessment['tier'] =
    best >= 4.5 ? 'pass_aa' : best >= 3 ? 'warn' : 'low';
  return { against_ink, against_white, best, text_colour, tier };
}
